import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import { apiGet, apiPost } from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import '../common/rtCommon.css';
import '../complaints/ComplaintManagement.css';
import './AddExpense.css';
import RTPageLayout from '../common/RTPageLayout';

const AddExpense = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [expenseDate, setExpenseDate] = useState(new Date());
  const [categoryId, setCategoryId] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [staffId, setStaffId] = useState('');
  const [description, setDescription] = useState('');
  const [categories, setCategories] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const selectedCategory = useMemo(
    () => categories.find((c) => String(c.id) === String(categoryId)),
    [categories, categoryId],
  );
  const isStaffExpense = Boolean(selectedCategory?.is_staff_expense);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [catsRes, methodsRes, staffRes] = await Promise.all([
          apiGet('/expenses/categories'),
          apiGet('/payments/methods?status=active'),
          apiGet('/expenses/staff-options'),
        ]);
        const cats = catsRes?.data || catsRes || [];
        const methods = methodsRes?.data || methodsRes || [];
        const staff = staffRes?.data || staffRes || [];
        setCategories(Array.isArray(cats) ? cats : []);
        setPaymentMethods(
          (Array.isArray(methods) ? methods : []).filter(
            (m) => m.status === 'ACTIVE' || m.status === 'active',
          ),
        );
        setStaffOptions(Array.isArray(staff) ? staff : []);
      } catch (err) {
        console.error(err);
        toastError('Failed to load expense form data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!isStaffExpense) {
      setStaffId('');
    }
  }, [isStaffExpense]);

  const validate = () => {
    const next = {};
    if (!expenseDate) next.expenseDate = 'Date is required';
    if (!categoryId) next.categoryId = 'Expense category is required';
    if (!amount || Number(amount) <= 0)
      next.amount = 'Enter a valid amount greater than 0';
    if (!paymentMethod) next.paymentMethod = 'Payment method is required';
    if (isStaffExpense && !staffId)
      next.staffId = 'Staff is required for this category';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setSaving(true);
      const yyyy = expenseDate.getFullYear();
      const mm = String(expenseDate.getMonth() + 1).padStart(2, '0');
      const dd = String(expenseDate.getDate()).padStart(2, '0');

      await apiPost('/expenses/', {
        expense_date: `${yyyy}-${mm}-${dd}`,
        category_id: Number(categoryId),
        amount: Number(amount),
        payment_method: paymentMethod,
        staff_id: isStaffExpense ? Number(staffId) : null,
        notes: description.trim() || null,
      });

      toastSuccess('Expense recorded successfully');
      navigate('/payments/view-expenses');
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        'Failed to save expense';
      toastError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <RTPageLayout
        activePath={location.pathname}
        title="Add Expense"
        actions={
          <RTButton variant="secondary" onClick={() => navigate('/payments')}>
            ← Back
          </RTButton>
        }
        mainSection={true}
      >
        <RTSpinner message="Loading..." />
      </RTPageLayout>
    );
  }

  return (
    <RTPageLayout
      activePath={location.pathname}
      title="Add Expense"
      actions={
        <>
          <RTButton variant="secondary" onClick={() => navigate('/payments')}>
            ← Back
          </RTButton>
        </>
      }
      mainSection={true}
    >
      <form onSubmit={handleSubmit} className="complaint-form">
        <div className="complaint-form-sections">
          <div className="complaint-section">
            <h3 className="complaint-section-title">Expense Details</h3>

            <div className="complaint-form-group">
              <label>
                Date <span style={{ color: 'red' }}>*</span>
              </label>
              <DatePicker
                selected={expenseDate}
                onChange={(date) => setExpenseDate(date)}
                dateFormat="dd/MM/yyyy"
                className="custom-datepicker"
                maxDate={new Date()}
                wrapperClassName="expense-datepicker-wrapper"
              />
              {errors.expenseDate && (
                <span className="form-error">{errors.expenseDate}</span>
              )}
            </div>

            <div className="complaint-form-group">
              <label>
                Expense Category <span style={{ color: 'red' }}>*</span>
              </label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
              >
                <option value="">Select category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                    {cat.is_staff_expense ? ' (Staff)' : ''}
                  </option>
                ))}
              </select>
              {errors.categoryId && (
                <span className="form-error">{errors.categoryId}</span>
              )}
              {categories.length === 0 && (
                <span className="form-error">
                  No active categories. Ask Admin/Owner to add them under
                  Settings → Manage Expense Categories.
                </span>
              )}
            </div>

            {isStaffExpense && (
              <div className="complaint-form-group">
                <label>
                  Staff <span style={{ color: 'red' }}>*</span>
                </label>
                <select
                  value={staffId}
                  onChange={(e) => setStaffId(e.target.value)}
                >
                  <option value="">Select staff</option>
                  {staffOptions.map((s) => (
                    <option key={s.staff_id} value={s.staff_id}>
                      {s.name}
                      {s.job_type ? ` — ${s.job_type}` : ''}
                    </option>
                  ))}
                </select>
                {errors.staffId && (
                  <span className="form-error">{errors.staffId}</span>
                )}
              </div>
            )}

            <div className="complaint-form-group">
              <label>
                Amount <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
              />
              {errors.amount && (
                <span className="form-error">{errors.amount}</span>
              )}
            </div>

            <div className="complaint-form-group">
              <label>
                Payment Method <span style={{ color: 'red' }}>*</span>
              </label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <option value="">Select payment method</option>
                {paymentMethods.map((m) => (
                  <option key={m.id} value={m.name}>
                    {m.name}
                  </option>
                ))}
              </select>
              {errors.paymentMethod && (
                <span className="form-error">{errors.paymentMethod}</span>
              )}
            </div>

            <div className="complaint-form-group">
              <label>Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional notes about this expense"
                rows={3}
                maxLength={500}
              />
              <div
                style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}
              >
                {description.length}/500 characters
              </div>
            </div>
          </div>
        </div>

        <div className="complaint-actions">
          <RTButton
            type="button"
            variant="secondary"
            onClick={() => navigate('/payments')}
            disabled={saving}
          >
            Cancel
          </RTButton>
          <RTButton type="submit" variant="primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Expense'}
          </RTButton>
        </div>
      </form>
    </RTPageLayout>
  );
};

export default AddExpense;
